import { AlertManager } from "client/Hooks";
import { open as openInLocalApp } from "client_legacy/Documents/SharePoint/Actions/openInLocalAppAction";
import { open as openWebDavInLocalApp } from "client_legacy/Documents/WebDav/localAppOpener";
import { open as openInWopi } from "client_legacy/Documents/WebDav/wopiOpener";
import { OpenFilePossibilitiesProvider } from "client/Features/Documents/WebDav/openFilePossibilitiesProvider";
import {
	downloadInline,
	openImagePreview,
	getWebDavFileRelativeUrl
} from "client/Features/Documents/WebDav/actions";
import {
	FileViewModel,
	ListLocationModel,
	ViewAndDocumentItemModel
} from "client/Api/Documents/SharePoint/common";
import { getItemsInPreferredOrder, UserEditorPreference } from "client_legacy/Documents/officeDocumentsOpenPreference";
import { calculate, getOpenLocalizations } from "client_legacy/Documents/SharePoint/documentOpenPossibilities";
import { localize } from "client_legacy/ndaw/culture";
import { downloadDocumentInline } from "client_legacy/Documents/SharePoint/Actions/downloadAction";
import { previewImageInDialogAction } from "client_legacy/Documents/SharePoint/Actions/previewImageInDialogAction";
import {
	documentsConfiguration,
	WebDavConfiguration
} from "client_legacy/ndaw/workspaceAbilitiesStatusProvider";
import { getExtension } from "client_legacy/Documents/path";
import {
	getOpenLocalizationsForExtension,
	OpenLocalizations
} from "client_legacy/Documents/openLocalizations";
import { showOpeningInLocalEditorWithOptionToOpenInOnlineEditor } from "client_legacy/Documents/misc";
import { openFileInSharePointViewer, openFileInWopi } from "client_legacy/Documents/SharePoint/documentOpener";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { getViewAndDocumentItemModelForLocation } from "client/Api/Documents/SharePoint/sharepointDocuments";
import { EditorModes, OfficeDocumentEditor } from "client/Api/Documents/common";
import {
	getWebDavModel,
	FileSystemItemModel,
	WebDavServerModel,
	WebDavModels
} from "client/Api/Documents/WebDav/webDav";
import { getDownloadFileUrlPath } from "client_legacy/Documents/SharePoint/UrlsProviders/documentsItemUrlsProviders";
import { openUrlFile } from "../Documents/openUrlFile";

export type SharePointOpenAction = {
	canExecute(documentItem: FileViewModel): boolean;
	execute(documentItem: FileViewModel): void;
};

export function getSharePointOpenActions(
	alertManager: AlertManager,
	editorPreference?: UserEditorPreference
): readonly SharePointOpenAction[] {
	return [
		...getOpenOfficeDocActions(
			alertManager,
			editorPreference
		),
		{
			canExecute: (c) => calculate(c).canBeOpenedAsExternalUrl,
			execute: guardAgainstInfectedDocuments(
				async (c) => {
					const url: string = getDownloadFileUrlPath(c.id, c.location);
					await openUrlFile(url, alertManager);
				},
				alertManager
			)
		},
		{
			canExecute: (c) => calculate(c).canBeOpenedInSharePointViewer,
			execute: guardAgainstInfectedDocuments(
				(c) => {
					openFileInSharePointViewer(c);
				},
				alertManager
			)
		},
		{
			canExecute: (c) => calculate(c).canBeOpenedInTheBrowser,
			execute: guardAgainstInfectedDocuments(
				(c) => {
					alertManager.showInfo(localize("_CannotOpenThisFileYet"));
					downloadDocumentInline(c);
				},
				alertManager
			)
		},
		{
			canExecute: (c) => calculate(c).canBeOpenedInImagePreviewDialog,
			execute: guardAgainstInfectedDocuments(
				(c) => {
					previewImageInDialogAction.execute(
						c,
						[c]
					);
				},
				alertManager
			)
		}
	];
}

export type WebDavOpenAction = {
	canExecute(webDavItem: FileSystemItemModel): boolean;
	execute(webDavItem: FileSystemItemModel): void;
};

const _openFilePossibilitiesProvider = new OpenFilePossibilitiesProvider();

export function getWebDavOpenActions(
	server: WebDavServerModel,
	webDavConfiguration: WebDavConfiguration,
	alertManager: AlertManager,
	editorPreference?: UserEditorPreference
): readonly WebDavOpenAction[] {

	return [
		...getWebDavOpenOfficeDocActions(
			server,
			webDavConfiguration,
			editorPreference
		),
		{
			canExecute: (c) => {
				if (c.isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						c.name,
						webDavConfiguration
					).externalUrl;
			},
			execute: async (c) => {
				const url: string = getWebDavFileRelativeUrl(c.id, server.id);
				await openUrlFile(url, alertManager);
			}
		},
		{
			canExecute: (c) => {
				if (c.isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						c.name,
						webDavConfiguration
					).browser;
			},
			execute: (c) => {
				downloadInline(
					server.id,
					c.id
				);
			}
		},
		{
			canExecute: (c) => {
				if (c.isFolder) {
					return false;
				}

				return _openFilePossibilitiesProvider
					.getForFileName(
						c.name,
						webDavConfiguration
					).imagePreviewDialog;
			},
			execute: (c) => {
				openImagePreview(
					server.id,
					c,
					[c]
				);
			}
		}
	];
}

function getWebDavOpenOfficeDocActions(
	server: WebDavServerModel,
	webDavConfiguration: WebDavConfiguration,
	editorPreference?: UserEditorPreference
): readonly WebDavOpenAction[] {
	return getItemsInPreferredOrder<WebDavOpenAction>(
		webDavConfiguration.defaultOfficeDocumentEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					canExecute: (c) => {
						if (c.isFolder) {
							return false;
						}

						return _openFilePossibilitiesProvider
							.getForFileName(
								c.name,
								webDavConfiguration
							).wopi;
					},
					execute: (c) => {
						openInWopi(
							server.id,
							c.id,
							c.name
						);
					}
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					canExecute: (c) => {
						if (c.isFolder) {
							return false;
						}

						return _openFilePossibilitiesProvider
							.getForFileName(
								c.name,
								webDavConfiguration
							).local;
					},
					execute: (c) => {
						openWebDavInLocalApp(server, c);
						const isWopiAvailable: boolean = _openFilePossibilitiesProvider
							.getForFileName(
								c.name,
								webDavConfiguration
							).wopi;

						if (isWopiAvailable) {
							const extension: string = getExtension(c.name);
							const localizations: OpenLocalizations = getOpenLocalizationsForExtension(extension);

							showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
								localizations,
								() => openInWopi(
									server.id,
									c.id,
									c.name
								)
							);
						}
					}
				})
			]
		],
		editorPreference
	);
}

function getOpenOfficeDocActions(
	alertManager: AlertManager,
	editorPreference: UserEditorPreference | undefined
): readonly SharePointOpenAction[] {
	const { sharePoint } = documentsConfiguration();

	return getItemsInPreferredOrder<SharePointOpenAction>(
		sharePoint.defaultOfficeDocumentEditor,
		[
			[
				OfficeDocumentEditor.Online,
				() => ({
					canExecute: (c) => calculate(c).canBeOpenedOnline,
					execute: guardAgainstInfectedDocuments(
						(c) => {
							openFileInWopi(
								c,
								undefined,
								undefined,
								EditorModes.View
							);
						},
						alertManager
					)
				})
			],
			[
				OfficeDocumentEditor.Local,
				() => ({
					canExecute: (c) => calculate(c).canBeOpenedLocally,
					execute: guardAgainstInfectedDocuments(
						(c) => {
							openInLocalApp(c);

							if (calculate(c).canBeOpenedOnline) {
								showOpeningInLocalEditorWithOptionToOpenInOnlineEditor(
									getOpenLocalizations(c),
									() => openFileInWopi(
										c,
										undefined,
										undefined,
										EditorModes.View
									)
								);
							}
						},
						alertManager
					)
				})
			]
		],
		editorPreference
	);
}

export function guardAgainstInfectedDocuments(
	handler: (context: FileViewModel) => void,
	alertManager: AlertManager
): ((context: FileViewModel) => void) {
	return (context: FileViewModel) => {
		if (context.isInfected) {
			alertManager.showWarning(
				localize("_ThisFileIsInfectedByMalwareToProtectYourPcYouCannotOpenShareOrDownloadThisFile")
			);
			return;
		}
		handler(context);
	};
}

export async function retrieveSharepointModels(
	location: ListLocationModel,
	itemId: number,
	alertManager: AlertManager,
	onChangeIsLoading: (isLoading: boolean) => void,
	signal: AbortSignal
): Promise<ViewAndDocumentItemModel> {
	try {
		onChangeIsLoading(true);

		const model: ViewAndDocumentItemModel = await getViewAndDocumentItemModelForLocation(
			location,
			itemId,
			signal
		);

		onChangeIsLoading(false);
		return model;
	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		alertManager.showError(
			localize("_TheDocumentCannotBeOpened")
		);

		onChangeIsLoading(false);
		throw e;
	}

};

export async function retrieveWebDavModels(
	serverId: number,
	fileId: string,
	alertManager: AlertManager,
	onChangeIsLoading: (isLoading: boolean) => void,
	signal: AbortSignal
): Promise<WebDavModels> {
	try {
		onChangeIsLoading(true);

		const model = await getWebDavModel(
			serverId,
			fileId,
			signal
		);

		onChangeIsLoading(false);
		return model;
	} catch (e) {
		if (isCancelledError(e)) {
			throw e;
		}

		alertManager.showError(
			localize("_TheDocumentCannotBeOpened")
		);

		onChangeIsLoading(false);
		throw e;
	}
};
